import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logError } from '../../../utils/logger';
import { Card, SectionTitle } from '../../shared/components';
import AttendanceCalendar from '../components/AttendanceCalendar';
import AttendanceSummaryChips from '../components/AttendanceSummaryChips';
import MonthPicker, { thisMonth, YearMonth } from '../components/MonthPicker';
import PunchCard from '../components/PunchCard';
import { AttendanceReport, Holiday, monthLabel, myApi } from '../payroll.api';

const KIND_LABEL: Record<string, string> = { NATIONAL: 'National', FESTIVAL: 'Festival', COMPANY: 'Company', OPTIONAL: 'Optional' };

/** Punch in/out, the month as a calendar, and the year's holidays — for whoever is signed in. */
export default function MyAttendancePage() {
  const navigate = useNavigate();
  const [ym, setYm] = useState<YearMonth>(thisMonth());
  const [report, setReport] = useState<AttendanceReport | null>(null);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    myApi
      .attendance(ym.year, ym.month)
      .then(setReport)
      .catch((e) => {
        setReport(null);
        setError(logError({ message: 'Could not load your attendance', error: e, code: 'MY_ATTENDANCE_FAILED' }));
      })
      .finally(() => setLoading(false));
  }, [ym]);

  useEffect(load, [load]);

  useEffect(() => {
    myApi
      .holidays(ym.year)
      .then(setHolidays)
      .catch(() => setHolidays([]));
  }, [ym.year]);

  const isCurrent = ym.year === thisMonth().year && ym.month === thisMonth().month;

  return (
    <Stack gap={3}>
      <SectionTitle
        title='MY ATTENDANCE'
        action={
          <Button variant='outlined' startIcon={<Icon icon='mdi:calendar-plus' />} onClick={() => navigate('/my-leaves')}>
            Apply for leave
          </Button>
        }
      />

      <PunchCard onChange={isCurrent ? load : undefined} />

      <Card
        title={monthLabel(ym.year, ym.month).toUpperCase()}
        action={<MonthPicker value={ym} onChange={setYm} />}
      >
        {loading && <LinearProgress />}
        {error && <Alert severity='error'>{error}</Alert>}
        {report && (
          <Stack gap={2}>
            <AttendanceSummaryChips summary={report.summary} />
            <AttendanceCalendar days={report.days} />
          </Stack>
        )}
      </Card>

      <Card title={`HOLIDAYS ${ym.year}`}>
        {holidays.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            No holidays declared for {ym.year} yet.
          </Typography>
        ) : (
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Day</TableCell>
                <TableCell>Holiday</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holidays.map((h) => {
                const d = new Date(`${h.holidayDate}T00:00:00`);
                return (
                  <TableRow key={h.id}>
                    <TableCell>{d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</TableCell>
                    <TableCell>{d.toLocaleDateString('en-IN', { weekday: 'long' })}</TableCell>
                    <TableCell>{h.name}</TableCell>
                    <TableCell>
                      <Chip size='small' label={`${KIND_LABEL[h.kind] ?? h.kind}${h.isPaid ? '' : ' · unpaid'}`} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}
